import { Analytics } from '@trello/atlassian-analytics';
import { hasValidInviteTokenForModel } from '@trello/invitation-tokens';
import { useBoardShortLink, useOrganizationName } from '@trello/routes';
import React, { useCallback, useMemo } from 'react';

import { BigNav } from './BigNav';
import { SmallNav } from './SmallNav';
import { useHasScrolled } from './useHasScrolled';
import { useLoggedOutHeaderBoardQuery } from './LoggedOutHeaderBoardQuery.generated';
import { useLoggedOutHeaderOrganizationQuery } from './LoggedOutHeaderOrganizationQuery.generated';

import './LoggedOutHeader.less';

enum ModelType {
  Board = 'Board',
  Organization = 'Organization',
}

export const LoggedOutHeader: React.FunctionComponent = () => {
  const boardShortLink = useBoardShortLink();
  const orgName = useOrganizationName();

  const { data: boardData } = useLoggedOutHeaderBoardQuery({
    variables: {
      shortLink: boardShortLink || '',
    },
    skip: !boardShortLink,
  });
  const { data: orgData } = useLoggedOutHeaderOrganizationQuery({
    variables: {
      orgName: orgName || '',
    },
    skip: !orgName,
  });

  const board = boardData?.board;
  const boardId = board?.id;
  const boardOrgId = board?.idOrganization;
  const org = orgData?.organization;
  const orgId = org?.id;

  const { modelName, analyticsContainers } = useMemo(() => {
    if (boardShortLink) {
      return {
        modelName: ModelType.Board,
        analyticsContainers: {
          board: {
            id: boardId,
          },
          ...(boardOrgId
            ? {
                organization: {
                  id: boardOrgId,
                },
              }
            : {}),
        },
      };
    } else if (orgName) {
      return {
        modelName: ModelType.Organization,
        analyticsContainers: {
          organization: {
            id: orgId,
          },
        },
      };
    }

    return { modelName: '', analyticsContainers: {} };
  }, [boardShortLink, orgName, boardId, boardOrgId, orgId]);

  const onSignupClick = useCallback(() => {
    if (
      modelName === ModelType.Board &&
      board &&
      hasValidInviteTokenForModel(board)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerSignupButton',
        source: 'acceptBoardInvitationScreen',
        containers: analyticsContainers,
      });
    } else if (
      modelName === ModelType.Organization &&
      org &&
      hasValidInviteTokenForModel(org)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerSignupButton',
        source: 'acceptTeamInvitationScreen',
        containers: analyticsContainers,
      });
    }
  }, [modelName, board, org, analyticsContainers]);

  const onLoginClick = useCallback(() => {
    if (
      modelName === ModelType.Board &&
      board &&
      hasValidInviteTokenForModel(board)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerLoginButton',
        source: 'acceptBoardInvitationScreen',
        containers: analyticsContainers,
      });
    } else if (
      modelName === ModelType.Organization &&
      org &&
      hasValidInviteTokenForModel(org)
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'headerLoginButton',
        source: 'acceptTeamInvitationScreen',
        containers: analyticsContainers,
      });
    }
  }, [modelName, board, org, analyticsContainers]);

  const hasScrolled = useHasScrolled(40);

  return (
    <>
      <BigNav
        hasScrolled={hasScrolled}
        onLoginClick={onLoginClick}
        onSignupClick={onSignupClick}
      />
      <SmallNav
        hasScrolled={hasScrolled}
        onLoginClick={onLoginClick}
        onSignupClick={onSignupClick}
      />
    </>
  );
};
